const jssConfig = require('./src/temp/config');
const plugins = require('./src/temp/next-config-plugins') || {};
const packageConfig = require('./package.json').config;
const { JSS_MODE_DISCONNECTED } = require('@sitecore-jss/sitecore-jss-nextjs');

const disconnectedServerUrl = `http://localhost:${process.env.PROXY_PORT || 3042}/`;
const isDisconnected = process.env.JSS_MODE === JSS_MODE_DISCONNECTED;

// A public URL (and uses below) is required for Sitecore Experience Editor support.
// This is set to http://localhost:3000 by default. See .env for more details.
const publicUrl = process.env.PUBLIC_URL;

const jss_home_root_id = process.env.JSS_HOME_ROOT_ID
  ? process.env.JSS_HOME_ROOT_ID
  : '{0157831C-0B70-4E22-B529-3FA81ECAB733}';
/**
 * @type {import('next').NextConfig}
 */
const nextConfig = {
  // Set assetPrefix to our public URL
  assetPrefix: publicUrl,
  productionBrowserSourceMaps: true,
  // Allow specifying a distinct distDir when concurrently running app in a container
  distDir: process.env.NEXTJS_DIST_DIR || '.next',

  // Make the same PUBLIC_URL available as an environment variable on the client bundle
  env: {
    PUBLIC_URL: publicUrl,
    JSS_HOME_ROOT_ID: jss_home_root_id,
  },

  i18n: {
    // These are all the locales you want to support in your application.
    // These should generally match (or at least be a subset of) those in Sitecore.
    locales: ['en', 'da-DK', 'es-ES', 'fr-FR'],
    // This is the locale that will be used when visiting a non-locale
    // prefixed path e.g. `/styleguide`.
    defaultLocale: packageConfig.language,
  },

  // Enable React Strict Mode
  reactStrictMode: true,

  // Disable the X-Powered-By header. Follows security best practices.
  poweredByHeader: false,

  async rewrites() {
    if (isDisconnected) {
      // When disconnected we proxy to the local faux layout service host, see scripts/disconnected-mode-server.js
      return [
        {
          source: '/robots.txt',
          destination: `/api/robots`,
        },
        {
          source: '/sitecore/:path*',
          destination: `${disconnectedServerUrl}/sitecore/:path*`,
        },
        {
          source: '/:locale/sitecore/:path*',
          destination: `${disconnectedServerUrl}/sitecore/:path*`,
        },
        // media items
        {
          source: '/data/media/:path*',
          destination: `${disconnectedServerUrl}/data/media/:path*`,
        },
        {
          source: '/:locale/data/media/:path*',
          destination: `${disconnectedServerUrl}/data/media/:path*`,
        },
      ];
    } else {
      // When in connected mode we want to proxy Sitecore paths off to Sitecore
      return [
        {
          source: '/robots.txt',
          destination: `/api/robots`,
        },
        {
          source: '/sitecore/:path*',
          destination: `${jssConfig.sitecoreApiHost}/sitecore/:path*`,
        },
        {
          source: '/:locale/sitecore/:path*',
          destination: `${jssConfig.sitecoreApiHost}/sitecore/:path*`,
        },

        // media items
        {
          source: '/-/:path*',
          destination: `${jssConfig.sitecoreApiHost}/-/:path*`,
        },
        {
          source: '/:locale/-/:path*',
          destination: `${jssConfig.sitecoreApiHost}/-/:path*`,
        },
        // visitor identification
        {
          source: '/layouts/:path*',
          destination: `${jssConfig.sitecoreApiHost}/layouts/:path*`,
        },
        {
          source: '/:locale/layouts/:path*',
          destination: `${jssConfig.sitecoreApiHost}/layouts/:path*`,
        },
        {
          source: '/api/jss/:path*',
          destination: `${jssConfig.sitecoreApiHost}/api/jss/:path*`,
        },

        // healthz check
        {
          source: '/healthz',
          destination: '/api/healthz',
        },
        // rewrite for Sitecore service pages
        {
          source: '/sitecore/service/:path*',
          destination: `${jssConfig.sitecoreApiHost}/sitecore/service/:path*`,
        },

        // API endpoints
        {
          source: '/sitecore/api/:path*',
          destination: `${jssConfig.sitecoreApiHost}/sitecore/api/:path*`,
        },
      ];
    }
  },

  webpack: (config, options) => {
    applyGraphQLCodeGenerationLoaders(config, options);
    config.module.rules.push({
      test: /\.svg$/i,
      issuer: /\.[jt]sx?$/,
      use: [
        {
          loader: '@svgr/webpack',
          options: {
            svgoConfig: {
              plugins: [
                {
                  name: 'removeViewBox',
                  active: false,
                },
              ],
            },
          },
        },
      ],
    });
    return config;
  },

  // use this configuration to ensure that only images from the whitelisted domains
  // can be served from the Next.js Image Optimization API
  // see https://nextjs.org/docs/app/api-reference/components/image#remotepatterns
  images: {
    remotePatterns: [
      {
        protocol: 'https',
        hostname: 'edge*.**',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'xmc-*.**',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'feaas*.blob.core.windows.net',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'osf-api1.dev.imagescape.com',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'osf-api.imagescape.com',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'osf-api1.live.imagescape.com',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'atlas.microsoft.com',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'osf-p-001.sitecorecontenthub.cloud',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'osf-q-001.sitecorecontenthub.cloud',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'freshpaint-video.com',
        port: '',
      },
    ],
    deviceSizes: [320, 414, 768, 1024, 1440, 1680, 2000],
    imageSizes: [],
  },

  experimental: {
    //largePageDataBytes: 128 * 1000, // 128KB by default
    largePageDataBytes: 512 * 100000,
    staleTimes: {
      dynamic: 0,
      static: 0,
    },
  },
};

const applyGraphQLCodeGenerationLoaders = (config, options) => {
  config.module.rules.push({
    test: /\.graphql$/,
    exclude: /node_modules/,
    use: [options.defaultLoaders.babel, { loader: 'graphql-let/loader' }],
  });

  config.module.rules.push({
    test: /\.graphqls$/,
    exclude: /node_modules/,
    use: ['graphql-let/schema/loader'],
  });

  config.module.rules.push({
    test: /\.ya?ml$/,
    type: 'json',
    use: 'yaml-loader',
  });

  return config;
};

module.exports = () => {
  // Run the base config through any configured plugins
  return Object.values(plugins).reduce(
    (acc, plugin) => plugin(acc),
    nextConfig
  );
};
